import React, { ReactNode } from 'react'
import { FactsTable, FactsTableRow } from '../../pages/components/FactsTable'
import { formatDateYYYYMMDD } from '../../packages/utils/numberFormatters'
import { translateSourcetype } from '../utils/translateSourcetype'
import IconButton from '@mui/material/IconButton'
import { Link } from 'react-router-dom'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Stack } from '@mui/system'
import { prepareSourceUrl } from '../utils/prepareSourceUrl'
import { SourceType } from '../../types/SourceType'
import useAuth from '../../hooks/useAuth'
import { getPressReleaseLink } from '../../utils/get-press-release-link'

export type ExtendedWarrantSourceType = SourceType | 'PublicationWarrant'

export const ExternalLink = ({ url, children }: { url: string; children: React.ReactNode }) => {
    return (
        <Link to={prepareSourceUrl(url)} target='_blank' rel='noopener noreferrer'>
            {children}
            <IconButton size='small' sx={{ ml: 1 }}>
                <OpenInNewIcon fontSize='small' />
            </IconButton>
        </Link>
    )
}

const Label = ({
    companyId,
    prId,
    sourceType,
    sourceUrl,
    title,
}: {
    companyId: string
    prId: string | null
    sourceType: ExtendedWarrantSourceType
    sourceUrl?: string | null
    title: string | null
}) => {
    const { user } = useAuth()

    return (
        <>
            <Stack direction='row' alignItems='center'>
                {sourceUrl ? <ExternalLink url={sourceUrl}>{translateSourcetype(sourceType)}</ExternalLink> : null}
                {!sourceUrl && title ? title : null}
            </Stack>
            {user?.groups?.includes('superadmin') && (
                <Stack direction='row' alignItems='center'>
                    <p>
                        Intern länk:{' '}
                        {!prId ? (
                            'Pressmeddelanden saknas'
                        ) : (
                            <Link to={getPressReleaseLink(companyId, prId)}>{translateSourcetype(sourceType)}</Link>
                        )}
                    </p>
                </Stack>
            )}
        </>
    )
}

interface SourcesTableProps {
    companyId: string
    sources: {
        id: string
        prId: string | null
        publishedDate: string
        sourceType: ExtendedWarrantSourceType
        sourceUrl: null | string
        title: null | string
    }[]
}

const EXCLUDED_SOURCE_TYPES: ExtendedWarrantSourceType[] = [
    'Prospectus',
    'Memorandum',
    'Supplement',
    'InformationDocument',
]

export const SourcesTable = ({ companyId, sources }: SourcesTableProps) => {
    const tableRows: ReactNode[] = sources
        .filter((source) => !EXCLUDED_SOURCE_TYPES.includes(source.sourceType))
        .sort((a, b) => {
            const dateA = new Date(a.publishedDate)
            const dateB = new Date(b.publishedDate)

            if (dateA < dateB) {
                return -1 // a comes before b (oldest first)
            }
            if (dateA > dateB) {
                return 1 // b comes before a (oldest first)
            }
            return 0 // dates are equal
        })
        .map((source) => {
            return (
                <FactsTableRow
                    key={source.id}
                    label={
                        <Label
                            companyId={companyId}
                            prId={source.prId}
                            sourceType={source.sourceType}
                            sourceUrl={source.sourceUrl}
                            title={source.title}
                        />
                    }
                >
                    {formatDateYYYYMMDD(source.publishedDate)}
                </FactsTableRow>
            )
        })

    return <FactsTable rows={tableRows} title='Källor' />
}
